import { Runtime, Cause, ParseResult } from "effect";

const refusalTags = [
  "InspectionAnswerVersionConflict",
  "InspectionAnswerRiskVersionConflict",
  "InspectionRiskVersionConflict",
  "InspectionVersionConflict",
  "InspectionAnswerGone",
  "InspectionAnswerRiskGone",
  "InspectionRiskGone",
  "InspectionNotFound",
  "InspectionIdTaken",
  "RiskInstanceVersionConflict",
  "RiskInstanceGone",
  "RiskInstanceNotFound",
  "InspectionAnswerNotFound",
  "InspectionAnswerRiskNotFound",
  "InspectionRiskNotFound",
  "InspectionQuestionnaireLinkNotFound",
  "RiskGroupNotFound",
  "MeasureTemplateNotFound",
  "MeasureNotFound",
  "MeasureNotDeletable",
  "InvalidRecurrenceConfig",
  "ContractMismatch",
  "Forbidden",
  "Unauthorized",
  "LEGACY_INSPECTION_CREATE_REVIEW_REQUIRED",
];

export interface FailureDiagnostic {
  reason?: string;
  traceId?: string;
  status?: number;
  contractIssues?: Array<{ path: string; kind: string }>;
}
const retained = new WeakMap<object, FailureDiagnostic>();
export function retainFailureDiagnostic(
  error: unknown,
  diagnostic: FailureDiagnostic,
) {
  if (error && typeof error === "object") retained.set(error, diagnostic);
}

/** Safe request metadata survives wrappers; bodies, URLs and error messages do not. */
export function failureDiagnostic(error: unknown): FailureDiagnostic {
  let current = Runtime.isFiberFailure(error)
    ? Cause.squash(error[Runtime.FiberFailureCauseId])
    : error;
  const result: FailureDiagnostic = {};
  for (
    let depth = 0;
    depth < 10 && current && typeof current === "object";
    depth++
  ) {
    Object.assign(result, retained.get(current));
    if (ParseResult.isParseError(current)) {
      result.contractIssues = ParseResult.ArrayFormatter.formatErrorSync(
        current,
      ).map((issue) => ({
        path: issue.path
          .map((part) =>
            typeof part === "number"
              ? "[]"
              : typeof part === "string" &&
                  /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(part)
                ? part
                : "[key]",
          )
          .join("."),
        kind: issue._tag,
      }));
    }
    const row = current as Record<string, any>;
    const reason = [row.reason, row.errorClass, row._tag].find(
      (value): value is string =>
        typeof value === "string" && refusalTags.includes(value),
    );
    if (reason) result.reason ??= reason;
    if (Array.isArray(row.contractIssues))
      result.contractIssues = row.contractIssues
        .filter(
          (issue: any) =>
            typeof issue?.path === "string" &&
            /^[a-zA-Z0-9_.[\]]*$/.test(issue.path) &&
            typeof issue?.kind === "string" &&
            /^[A-Za-z]+$/.test(issue.kind),
        )
        .map((issue: any) => ({ path: issue.path, kind: issue.kind }));
    const trace =
      row.traceId ??
      row.response?.headers?.["x-sync-trace-id"] ??
      row.request?.headers?.["sentry-trace"]?.split("-")[0];
    if (
      typeof trace === "string" &&
      /^[a-f0-9]{32}$/.test(trace) &&
      !/^0+$/.test(trace)
    )
      result.traceId ??= trace;
    const status = row.status ?? row.response?.status;
    if (typeof status === "number" && status >= 100 && status <= 599)
      result.status ??= status;
    current = row.cause;
  }
  return result;
}

export function refusalReason(text = ""): string {
  return (
    refusalTags.find((tag) => text.includes(tag)) ??
    "UnclassifiedTerminalRefusal"
  );
}
